import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { motion } from 'framer-motion';
import { Scale, Shield, FileText, Mail } from 'lucide-react';

const LegalPage = () => {
  const { language } = useLanguage();
  const { isDark } = useTheme();

  const t = {
    fr: {
      title: "Mentions Légales",
      subtitle: "Informations juridiques et politique de confidentialité",
      lastUpdate: "Dernière mise à jour",
      sections: [
        {
          icon: FileText,
          title: "Éditeur du site",
          content: `**Alliance Démocratique Africaine (ADA)**
Parti politique déclaré et autorisé en Côte d'Ivoire depuis septembre 2024.

**Siège social :** Abidjan, Côte d'Ivoire
**Email :** contact@ada.org
**Directeur de la publication :** Alpha Diallo, Président

Ce site est édité par l'Alliance Démocratique Africaine dans le cadre de sa communication politique et de son engagement citoyen.`
        },
        {
          icon: Scale,
          title: "Statut juridique",
          content: `L'Alliance Démocratique Africaine est un parti politique légalement constitué conformément à la législation ivoirienne sur les partis politiques.

**Numéro d'enregistrement :** [En attente de publication]
**Date d'autorisation :** Septembre 2024

Le parti est soumis aux obligations légales en matière de financement politique et de transparence.`
        },
        {
          icon: Shield,
          title: "Protection des données personnelles",
          content: `**Collecte de données**
Nous collectons uniquement les données que vous nous fournissez volontairement :
- Formulaires de contact et d'adhésion
- Inscription à la newsletter
- Dons et contributions

**Utilisation des données**
Vos données sont utilisées exclusivement pour :
- Vous informer des activités du parti
- Gérer votre adhésion le cas échéant
- Traiter vos dons et délivrer les reçus fiscaux

**Droits des utilisateurs**
Conformément à la réglementation, vous disposez d'un droit d'accès, de rectification et de suppression de vos données. Pour exercer ces droits, contactez-nous à : contact@ada.org

**Cookies**
Ce site utilise des cookies techniques nécessaires à son fonctionnement. Aucun cookie publicitaire n'est utilisé.`
        },
        {
          icon: Mail,
          title: "Contact et réclamations",
          content: `Pour toute question concernant ce site, son contenu ou l'utilisation de vos données personnelles :

**Email :** contact@ada.org
**Adresse :** Siège de l'ADA, Abidjan, Côte d'Ivoire

Nous nous engageons à répondre à toute demande dans un délai de 30 jours.`
        }
      ],
      copyright: "Propriété intellectuelle",
      copyrightText: "L'ensemble des contenus de ce site (textes, images, logos, documents) sont la propriété de l'Alliance Démocratique Africaine ou sont utilisés avec autorisation. Toute reproduction non autorisée est interdite.",
      disclaimer: "Les opinions exprimées sur ce site sont celles de l'ADA et n'engagent que le parti."
    },
    en: {
      title: "Legal Notice",
      subtitle: "Legal information and privacy policy",
      lastUpdate: "Last updated",
      sections: [
        {
          icon: FileText,
          title: "Website Publisher",
          content: `**African Democratic Alliance (ADA)**
Political party declared and authorized in Côte d'Ivoire since September 2024.

**Headquarters:** Abidjan, Côte d'Ivoire
**Email:** contact@ada.org
**Publication Director:** Alpha Diallo, President

This website is published by the African Democratic Alliance as part of its political communication and civic engagement.`
        },
        {
          icon: Scale,
          title: "Legal Status",
          content: `The African Democratic Alliance is a legally constituted political party in accordance with Ivorian legislation on political parties.

**Registration number:** [Pending publication]
**Authorization date:** September 2024

The party is subject to legal obligations regarding political funding and transparency.`
        },
        {
          icon: Shield,
          title: "Personal Data Protection",
          content: `**Data Collection**
We only collect data that you voluntarily provide:
- Contact and membership forms
- Newsletter subscription
- Donations and contributions

**Data Use**
Your data is used exclusively to:
- Inform you about party activities
- Manage your membership if applicable
- Process your donations and issue tax receipts

**User Rights**
In accordance with regulations, you have the right to access, rectify, and delete your data. To exercise these rights, contact us at: contact@ada.org

**Cookies**
This site uses technical cookies necessary for its operation. No advertising cookies are used.`
        },
        {
          icon: Mail,
          title: "Contact and Complaints",
          content: `For any questions regarding this site, its content, or the use of your personal data:

**Email:** contact@ada.org
**Address:** ADA Headquarters, Abidjan, Côte d'Ivoire

We commit to responding to any request within 30 days.`
        }
      ],
      copyright: "Intellectual Property",
      copyrightText: "All content on this site (texts, images, logos, documents) are the property of the African Democratic Alliance or are used with permission. Any unauthorized reproduction is prohibited.",
      disclaimer: "The opinions expressed on this site are those of the ADA and only represent the party's views."
    },
    pt: {
      title: "Avisos Legais",
      subtitle: "Informações jurídicas e política de privacidade",
      lastUpdate: "Última atualização",
      sections: [
        {
          icon: FileText,
          title: "Editor do Site",
          content: `**Aliança Democrática Africana (ADA)**
Partido político declarado e autorizado na Costa do Marfim desde setembro de 2024.

**Sede:** Abidjan, Costa do Marfim
**Email:** contact@ada.org
**Diretor de Publicação:** Alpha Diallo, Presidente

Este site é publicado pela Aliança Democrática Africana no âmbito da sua comunicação política e compromisso cívico.`
        },
        {
          icon: Scale,
          title: "Estatuto Jurídico",
          content: `A Aliança Democrática Africana é um partido político legalmente constituído de acordo com a legislação marfinense sobre partidos políticos.

**Número de registo:** [Pendente de publicação]
**Data de autorização:** Setembro de 2024

O partido está sujeito às obrigações legais em matéria de financiamento político e transparência.`
        },
        {
          icon: Shield,
          title: "Proteção de Dados Pessoais",
          content: `**Recolha de Dados**
Recolhemos apenas os dados que nos fornece voluntariamente:
- Formulários de contacto e adesão
- Inscrição na newsletter
- Doações e contribuições

**Utilização dos Dados**
Os seus dados são utilizados exclusivamente para:
- Informá-lo sobre as atividades do partido
- Gerir a sua adesão, se aplicável
- Processar as suas doações e emitir recibos fiscais

**Direitos dos Utilizadores**
De acordo com a regulamentação, tem o direito de acesso, retificação e eliminação dos seus dados. Para exercer estes direitos, contacte-nos em: contact@ada.org

**Cookies**
Este site utiliza cookies técnicos necessários ao seu funcionamento. Não são utilizados cookies publicitários.`
        },
        {
          icon: Mail,
          title: "Contacto e Reclamações",
          content: `Para qualquer questão sobre este site, o seu conteúdo ou a utilização dos seus dados pessoais:

**Email:** contact@ada.org
**Morada:** Sede da ADA, Abidjan, Costa do Marfim

Comprometemo-nos a responder a qualquer pedido no prazo de 30 dias.`
        }
      ],
      copyright: "Propriedade Intelectual",
      copyrightText: "Todo o conteúdo deste site (textos, imagens, logótipos, documentos) são propriedade da Aliança Democrática Africana ou são utilizados com autorização. Qualquer reprodução não autorizada é proibida.",
      disclaimer: "As opiniões expressas neste site são as da ADA e apenas representam as posições do partido."
    }
  };

  const content = t[language] || t.fr;

  const formatContent = (text) => {
    return text.split('\n').map((line, index) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        return <strong key={index} className="block mt-4 mb-1">{line.replace(/\*\*/g, '')}</strong>;
      }
      if (line.startsWith('**')) {
        const parts = line.split('**');
        return (
          <p key={index} className="mb-1">
            <strong>{parts[1]}</strong>{parts[2]}
          </p>
        );
      }
      if (line.startsWith('- ')) {
        return <li key={index} className="ml-4">{line.substring(2)}</li>;
      }
      if (line.trim() === '') return <br key={index} />;
      return <p key={index} className="mb-2">{line}</p>;
    });
  };

  return (
    <div className={`min-h-screen pt-20 ${isDark ? 'bg-[#1A0F2E]' : 'bg-[#FAFAFA]'}`}>
      {/* Hero */}
      <section className={`py-12 ${isDark ? 'bg-[#2D1B4E]' : 'bg-white'} border-b ${isDark ? 'border-white/10' : 'border-gray-200'}`}>
        <div className="container mx-auto px-4 lg:px-10">
          <div className="max-w-3xl">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`text-3xl md:text-4xl font-bold mb-2 ${isDark ? 'text-white' : 'text-[#1A0F2E]'}`}
            >
              {content.title}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className={`${isDark ? 'text-white/60' : 'text-gray-600'}`}
            >
              {content.subtitle}
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className={`text-sm mt-4 ${isDark ? 'text-white/40' : 'text-gray-400'}`}
            >
              {content.lastUpdate}: Janvier 2025
            </motion.p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 lg:px-10 max-w-4xl">
          <div className="space-y-8">
            {content.sections.map((section, index) => {
              const Icon = section.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`p-6 rounded-xl ${isDark ? 'bg-[#2D1B4E]' : 'bg-white'} shadow-sm`}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-[#5B2D8E]/10 flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#5B2D8E]" />
                    </div>
                    <h2 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-[#1A0F2E]'}`}>
                      {section.title}
                    </h2>
                  </div>
                  <div className={`text-sm leading-relaxed ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                    {formatContent(section.content)}
                  </div>
                </motion.div>
              );
            })}

            {/* Copyright */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className={`p-6 rounded-xl ${isDark ? 'bg-[#2D1B4E]' : 'bg-white'} shadow-sm`}
            >
              <h2 className={`text-xl font-bold mb-4 ${isDark ? 'text-white' : 'text-[#1A0F2E]'}`}>
                {content.copyright}
              </h2>
              <p className={`text-sm ${isDark ? 'text-white/70' : 'text-gray-600'}`}>
                {content.copyrightText}
              </p>
            </motion.div>

            {/* Disclaimer */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className={`text-center text-sm italic ${isDark ? 'text-white/40' : 'text-gray-400'}`}
            >
              {content.disclaimer}
            </motion.p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LegalPage;
